import type { OfferSupplierGroup } from '@progear/finance-core';

/**
 * Offer-viewer highlight engine — the PRESENTATION layer that lights up the real
 * Erply printout as the user scans an offer's supplier breakdown (module 1.3, the
 * rich offer view; Part B). finance-core deliberately keeps colours/labels OUT of
 * the money layer (master rule), so the palette, the reserved hover/confirm
 * colours, and the paint protocol live here. Nothing in this file touches money —
 * it only maps finance-core's supplier order to colours and builds the messages
 * the injected in-iframe script paints from.
 *
 * The engine runs INSIDE the printout iframe (`sandbox="allow-scripts"`, never
 * `allow-same-origin`): {@link injectOfferHighlight} splices the script into the
 * sanitized HTML, the script indexes the printout rows by product code (the
 * printout's 2nd cell) and repaints on each `postMessage` from the parent. The
 * parent only ever accepts the script's `ready` handshake from that one iframe
 * window (see {@link isOfferReadyMessage}) — the security boundary.
 */

/** Sentinel key for the single "unassigned" bucket (Erply sent no supplier). The
 *  finance-core group carries a null `supplierExternalId`; the view needs a stable
 *  string key for its hovered/confirmed sets, so null collapses to this. */
export const NO_SUPPLIER_KEY = '__none__';

/** The stable set/highlight key for a supplier group — its Erply id, or the
 *  unassigned sentinel when finance-core sent a null grouping key. */
export function supplierKey(supplierExternalId: string | null): string {
  return supplierExternalId ?? NO_SUPPLIER_KEY;
}

/** A precise printout-row address: the 1-based printout position (`rowIndex`
 *  from the offer-line mirror) plus the row's product code. The engine trusts
 *  the row position only when the printout row's code cell matches `code`
 *  (mirror-drift guard); a code alone is honoured only when it is UNIQUE in
 *  the printout — generic codes (TR-001 "Tarvikud") repeat dozens of times,
 *  so a code-only match would light a foreign row (task 89's bug). */
export interface PrintoutRowRef {
  /** 1-based printout position (offer_line.row_index). */
  row: number;
  code: string;
}

/** One repaint instruction the injected script applies to a printout row.
 *  Matched by `row` (1-based printout position, code-verified) when present,
 *  else by product code IF that code is unique in the printout. `bg`/`accent`
 *  are empty strings to CLEAR a row. */
export interface OfferPaintRow {
  code: string;
  /** 1-based printout position — the precise row key (duplicate-code safe). */
  row?: number;
  bg: string;
  accent: string;
  check: boolean;
}

/** The ephemeral, VIEW-ONLY state that drives the highlight — which supplier is
 *  hovered, whether every supplier is colour-marked, and which suppliers the user
 *  has ticked as visually verified during THIS review. It persists nothing (it is
 *  a review aid, like a hover), so it lives in component state, not Supabase. */
export interface OfferHighlightState {
  hovered: string | null;
  marked: boolean;
  confirmed: ReadonlySet<string>;
}

/** Stable highlight palette, assigned to supplier groups by finance-core order
 *  (net descending). Yellow and green are RESERVED for the hover and confirm
 *  states below, so they are never reused as a group colour. */
export const OFFER_PALETTE = [
  { bg: '#dbeafe', accent: '#2563eb', swatch: '#3b82f6' },
  { bg: '#fce7f3', accent: '#db2777', swatch: '#ec4899' },
  { bg: '#ffedd5', accent: '#ea580c', swatch: '#f97316' },
  { bg: '#ede9fe', accent: '#7c3aed', swatch: '#8b5cf6' },
  { bg: '#cffafe', accent: '#0891b2', swatch: '#06b6d4' },
  { bg: '#fee2e2', accent: '#dc2626', swatch: '#ef4444' },
  { bg: '#e0e7ff', accent: '#4f46e5', swatch: '#6366f1' },
  { bg: '#e2e8f0', accent: '#475569', swatch: '#64748b' },
] as const;

/** Hover colour (reserved) — the supplier the cursor is on flashes yellow. */
export const OFFER_HOVER = { bg: '#fef08a', accent: '#ca8a04' } as const;
/** Confirm colour (reserved) — a ticked supplier's rows go green with a ✓. */
export const OFFER_CONFIRM = { bg: '#bbf7d0', accent: '#16a34a' } as const;

/** The palette colour for a supplier group at `index` in finance-core order,
 *  wrapping when there are more groups than colours. */
export function offerPaletteColor(index: number): (typeof OFFER_PALETTE)[number] {
  return OFFER_PALETTE[index % OFFER_PALETTE.length];
}

/**
 * Compute the per-row repaint instructions for the whole printout from the current
 * view state. Precedence per supplier group: confirmed (green + ✓) → hovered
 * (yellow) → marked (its palette colour) → cleared. Every line in a group gets its
 * group's instruction, keyed by product code (the printout join key). Pure — no
 * money, no I/O.
 */
export function computeOfferPaintRows(
  groups: readonly OfferSupplierGroup[],
  state: OfferHighlightState,
): OfferPaintRow[] {
  const rows: OfferPaintRow[] = [];
  groups.forEach((group, index) => {
    const key = supplierKey(group.supplierExternalId);
    let bg = '';
    let accent = '';
    let check = false;
    if (state.confirmed.has(key)) {
      bg = OFFER_CONFIRM.bg;
      accent = OFFER_CONFIRM.accent;
      check = true;
    } else if (state.hovered === key) {
      bg = OFFER_HOVER.bg;
      accent = OFFER_HOVER.accent;
    } else if (state.marked) {
      const color = offerPaletteColor(index);
      bg = color.bg;
      accent = color.accent;
    }
    for (const line of group.lines) {
      // A code-less line (rare Erply freetext / discount row) has no printout row
      // to key on — matching is by product code — so it is never painted.
      if (line.productCode === null) continue;
      rows.push({ code: line.productCode, row: line.rowIndex, bg, accent, check });
    }
  });
  return rows;
}

/** The product code the printout should scroll to when a supplier is hovered — its
 *  first line's code — or undefined when nothing is hovered / the group has no
 *  lines / it is not found. */
export function offerScrollCode(
  groups: readonly OfferSupplierGroup[],
  hovered: string | null,
): string | undefined {
  if (hovered === null) return undefined;
  const group = groups.find((g) => supplierKey(g.supplierExternalId) === hovered);
  // Scroll to the group's first line that HAS a code (a code-less line has no
  // printout row to scroll to); undefined when none / the group is not found.
  return group?.lines.find((line) => line.productCode !== null)?.productCode ?? undefined;
}

/** The precise row ref the printout should scroll to when a supplier is
 *  hovered — its first coded line's `{row, code}` — or undefined. */
export function offerScrollRef(
  groups: readonly OfferSupplierGroup[],
  hovered: string | null,
): PrintoutRowRef | undefined {
  if (hovered === null) return undefined;
  const group = groups.find((g) => supplierKey(g.supplierExternalId) === hovered);
  const line = group?.lines.find((l) => l.productCode !== null);
  return line === undefined ? undefined : { row: line.rowIndex, code: line.productCode! };
}

const OFFER_MSG_MARKER = '__pgOffer';
const OFFER_READY = 'ready';
const OFFER_PAINT = 'paint';

/** The full `paint` message the parent posts to the printout iframe for a given
 *  view state — the repaint instructions plus the code to scroll into view. */
export function buildOfferPaintMessage(
  groups: readonly OfferSupplierGroup[],
  state: OfferHighlightState,
) {
  return {
    [OFFER_MSG_MARKER]: OFFER_PAINT,
    rows: computeOfferPaintRows(groups, state),
    scrollCode: offerScrollCode(groups, state.hovered),
    scrollRef: offerScrollRef(groups, state.hovered),
  };
}

/** A relations-overlay highlight target: the printout row position when it is
 *  known (invoice-line mirror `rowIndex` — duplicate-code safe), else a bare
 *  code the engine honours only when unique in the printout. */
export interface RowHighlightRef {
  code?: string;
  /** 1-based printout position, when the caller knows the exact row. */
  row?: number;
}

/** A relations-overlay paint message: light the given rows in the reserved
 *  hover yellow and scroll the first one into view. Used by the worktable's
 *  Seosed overlay, which frames the REAL Erply invoice printout and
 *  highlights the clicked offer row's invoice lines. Row-keyed refs resolve
 *  precisely even for generic repeated codes (TR-001); code-only refs fall
 *  back to the engine's unique-code rule. */
export function buildRowHighlightMessage(refs: readonly RowHighlightRef[]) {
  return {
    [OFFER_MSG_MARKER]: OFFER_PAINT,
    rows: refs.map((ref) => ({
      code: ref.code ?? '',
      row: ref.row,
      bg: OFFER_HOVER.bg,
      accent: OFFER_HOVER.accent,
      check: false,
    })),
    scrollRef: refs[0],
  };
}

/** True only for the injected script's `ready` handshake — the parent trusts it
 *  ONLY after also checking the message came from the iframe's own window. */
export function isOfferReadyMessage(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>)[OFFER_MSG_MARKER] === OFFER_READY
  );
}

/**
 * The engine script + styles, spliced into the sanitized Erply printout HTML just
 * before `</body>` (appended when there is no body tag). Runs inside the
 * `sandbox="allow-scripts"` iframe: it indexes `tr.invoice_row` by product code
 * (the row's 2nd cell), repaints on each parent `paint` message, and announces
 * `ready` back to the parent. This mirrors the approved canvas exactly.
 */
export const OFFER_HIGHLIGHT_SCRIPT = `
<style>
  tr.invoice_row td { transition: background-color 120ms ease; }
  .pg-check { margin-right: 3px; font-weight: 700; }
  tr.pg-delivered td { text-decoration: line-through; opacity: 0.5; }
  tr.pg-partial td:first-child { box-shadow: inset 4px 0 0 #d97706; }
  tr.pg-partial td { background-color: rgba(217, 119, 6, 0.12); }
  .pg-partial-badge { margin-left: 4px; font-size: 0.75em; font-weight: 700; color: #b45309; white-space: nowrap; }
  tr.pg-reserved td:first-child { box-shadow: inset 4px 0 0 #ca8a04; }
  tr.pg-reserved td { background-color: rgba(250, 204, 21, 0.22); }
  .pg-reserved-badge { margin-left: 4px; font-size: 0.75em; font-weight: 700; color: #a16207; white-space: nowrap; }
</style>
<script>
(function () {
  var rows = Array.prototype.slice.call(document.querySelectorAll('tr.invoice_row'));
  var byCode = {};
  var codeCount = {};
  var descriptiveRows = [];
  function codeOf(tr) {
    var tds = tr.querySelectorAll('td');
    return tds[1] ? (tds[1].textContent || '').trim() : '';
  }
  function isDescriptive(tr) {
    // A descriptive heading row has NO code and NO real figures — its name
    // cell (3rd) carries the heading text, and every cell after it is blank
    // or a zero amount (real Erply printouts render "0,0000" / "0" / "0,00"
    // in the qty/price/sum cells of heading rows).
    var tds = tr.querySelectorAll('td');
    if (codeOf(tr) !== '') return false;
    for (var i = 3; i < tds.length; i++) {
      var t = (tds[i].textContent || '').replace(/\\s/g, '');
      if (t !== '' && !/^0([.,]0+)?(%|\u20ac)?$/.test(t)) return false;
    }
    return true;
  }
  rows.forEach(function (tr) {
    if (isDescriptive(tr)) descriptiveRows.push(tr);
    else {
      var c = codeOf(tr);
      byCode[c] = tr;
      codeCount[c] = (codeCount[c] || 0) + 1;
    }
  });

  // Resolve one row address: prefer the 1-based printout position (verified
  // against the row's code cell — mirror drift never paints a wrong row);
  // fall back to the code ONLY when it is unique in the printout. A generic
  // code (TR-001 "Tarvikud") repeats, so a code-only ref resolves to nothing
  // rather than to a foreign row.
  function resolveRef(ref) {
    if (!ref) return null;
    if (typeof ref.row === 'number') {
      var tr = rows[ref.row - 1];
      if (tr && (!ref.code || codeOf(tr) === ref.code)) return tr;
    }
    if (ref.code && codeCount[ref.code] === 1) return byCode[ref.code];
    return null;
  }

  function paint(tr, ins) {
    var tds = tr.querySelectorAll('td');
    for (var i = 0; i < tds.length; i++) tds[i].style.backgroundColor = ins.bg || '';
    if (tds[0]) tds[0].style.boxShadow = ins.accent ? ('inset 4px 0 0 ' + ins.accent) : '';
    var mark = tr.querySelector('.pg-check');
    if (ins.check) {
      if (!mark) {
        mark = document.createElement('span');
        mark.className = 'pg-check';
        mark.textContent = '\u2713';
        if (tds[0]) tds[0].insertBefore(mark, tds[0].firstChild);
      }
      mark.style.color = ins.accent || '#16a34a';
    } else if (mark) {
      mark.parentNode.removeChild(mark);
    }
  }

  function setBadge(tr, on, cls, text) {
    var badge = tr.querySelector('.' + cls);
    if (on && !badge) {
      var tds = tr.querySelectorAll('td');
      var cell = tds[2] || tds[0];
      if (cell) {
        badge = document.createElement('span');
        badge.className = cls;
        badge.textContent = text;
        cell.appendChild(badge);
      }
    } else if (!on && badge) {
      badge.parentNode.removeChild(badge);
    }
  }

  window.addEventListener('message', function (e) {
    var msg = e.data;
    if (!msg || msg.${OFFER_MSG_MARKER} !== '${OFFER_PAINT}') return;
    if (Array.isArray(msg.hideRefs)) {
      rows.forEach(function (tr) { tr.style.display = ''; });
      msg.hideRefs.forEach(function (ref) {
        var tr = resolveRef(ref);
        if (tr) tr.style.display = 'none';
      });
    }
    if (msg.hideDescriptive !== undefined) {
      descriptiveRows.forEach(function (tr) {
        tr.style.display = msg.hideDescriptive ? 'none' : '';
      });
    }
    if (
      Array.isArray(msg.struckRefs) ||
      Array.isArray(msg.partialRefs) ||
      Array.isArray(msg.reservedRefs)
    ) {
      var struckTrs = (msg.struckRefs || []).map(resolveRef).filter(Boolean);
      var partialTrs = (msg.partialRefs || []).map(resolveRef).filter(Boolean);
      var reservedTrs = (msg.reservedRefs || []).map(resolveRef).filter(Boolean);
      rows.forEach(function (tr) {
        tr.className = tr.className.replace(/ ?pg-(delivered|partial|reserved)/g, '');
        // Precedence: a delivery marking always outranks the money marking —
        // struck (fully delivered) > partial (some delivered) > reserved
        // (EM-broneering: raha laekunud, tarnimata).
        var isStruck = struckTrs.indexOf(tr) !== -1;
        var isPartial = !isStruck && partialTrs.indexOf(tr) !== -1;
        var isReserved = !isStruck && !isPartial && reservedTrs.indexOf(tr) !== -1;
        if (isStruck) tr.className += ' pg-delivered';
        else if (isPartial) tr.className += ' pg-partial';
        else if (isReserved) tr.className += ' pg-reserved';
        setBadge(tr, isPartial, 'pg-partial-badge', 'OSALINE');
        setBadge(tr, isReserved, 'pg-reserved-badge', 'ETTEMAKS');
      });
    }
    (msg.rows || []).forEach(function (ins) {
      var tr = resolveRef(ins);
      if (tr) paint(tr, ins);
    });
    var scrollTr = resolveRef(msg.scrollRef || (msg.scrollCode ? { code: msg.scrollCode } : null));
    if (scrollTr) {
      scrollTr.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  });

  parent.postMessage({ ${OFFER_MSG_MARKER}: '${OFFER_READY}', codes: Object.keys(byCode) }, '*');
})();
</script>
`;

/** Splice the highlight engine into the sanitized printout HTML — before the
 *  closing body tag when present, else appended. */
export function injectOfferHighlight(html: string): string {
  return html.includes('</body>')
    ? html.replace('</body>', OFFER_HIGHLIGHT_SCRIPT + '</body>')
    : html + OFFER_HIGHLIGHT_SCRIPT;
}
